import { type ComponentType, type Ref, useImperativeHandle } from 'react'
import { FormProvider, type UseFormReturn } from 'react-hook-form'
import { type useForm } from '../hooks/useForm'
import { ValidationSchemaProvider } from './ValidationSchemaContext'

export interface WithFormProvider<Schema extends object> {
  formMethods: UseFormReturn<Schema>
  defaultValues?: Schema
  disabled?: boolean
  ref?: Ref<ReturnType<typeof useForm<Schema>>>
}

export type UseFormProps<Schema extends object> = Omit<WithFormProvider<Schema>, 'formMethods'>

export const withFormProvider = <Schema extends object, P extends Omit<WithFormProvider<Schema>, 'disabled'> = WithFormProvider<Schema>>(
  WrappedComponent: ComponentType<P>,
  useFormHook: (formProps: { defaultValues?: Schema; disabled?: boolean }) => ReturnType<typeof useForm<Schema>>,
) => {
  const ComponentWithFormProvider = ({ ref, ...props }: Omit<P & UseFormProps<Schema>, 'formMethods'>) => {
    const formResult = useFormHook(props)
    const { validationSchema, ...methods } = formResult

    useImperativeHandle(ref, () => formResult, [formResult])

    return (
      <ValidationSchemaProvider value={validationSchema}>
        <FormProvider {...methods}>
          <WrappedComponent {...(props as P)} formMethods={methods} />
        </FormProvider>
      </ValidationSchemaProvider>
    )
  }
  ComponentWithFormProvider.displayName = `WithFormProvider-${WrappedComponent.displayName || 'WrappedForm'}`
  return ComponentWithFormProvider
}
